import { useState, useMemo, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Box,
    LinearProgress,
    Checkbox
} from '@mui/material';
import { TableRow as CustomTableRow } from './TableRow';
import { TableDataGridProps, SortConfig, TFilters, Column } from './types';
import { useQuery } from 'react-query';
import axiosClient from '../../lib/axios';
import IRequest from '../../types/IRequest';
import { formatarObjRetorno } from './helpers';
import DialogHeadFilters from './DialogHeadFilters';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { processUrlFilters, processFilterValues } from './helpers';
import useSetParamsURL from '../../lib/useSetParamsURL';

export interface TableDataGridRef<T = any> {
    getSelectedRows: () => T[];
}

export const TableDataGridV2 = forwardRef(function TableDataGridV2<T extends Record<string, any>>(
    { columns, ...props }: TableDataGridProps<T>,
    ref: React.ForwardedRef<TableDataGridRef<T>>
) {
    const {
        data: initialData,
        url,
        urlParams = false,
        preFilledFilters = {},
        handleUpdatePreFilters,
        queryParams = {},
        queryKey = 'table-data',
        queryOptions = {},
        loading: externalLoading = false,
        enableSelection = false,
        enableSort = true,
        defaultRowsPerPage = 25,
        onRowDoubleClick,
        maxHeight = 500,
        pagination = "client",
        defaultSelectedFields,
        selectedFields,
        onSelectionChange,
        getLimitAndPage = () => { }
    } = props;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [filters, setFilters] = useState<TFilters>({})
    const [openDialogFilters, setOpenDialogFilters] = useState<Column<T> | null>(null)
    const [selectedRows, setSelectedRows] = useState<T[]>(defaultSelectedFields || []);

    const { setMultipleParamsUrl, searchParams, clearSingleParamUrl, clearParamsUrl } = useSetParamsURL();

    const queryParameters = useMemo(() => ({
        ...queryParams,
        ...(pagination === "server" && { page: page + 1 }),
        limit: rowsPerPage,
        ...(sortConfig && pagination === "server" && {
            ordering: JSON.stringify([{
                column: sortConfig.field,
                direction: sortConfig.direction.toUpperCase()
            }])
        }),
        ...formatarObjRetorno(filters)
    }), [queryParams, pagination, page, rowsPerPage, filters, sortConfig]);

    const { data, isFetching } = useQuery({
        queryKey: [
            queryKey,
            queryParameters
        ],
        queryFn: async () => {
            const response = await axiosClient.get(url!, {
                params: queryParameters
            });
            return response.data;
        },
        enabled: !!url,
        keepPreviousData: true,
        staleTime: 30000,
        cacheTime: 1000 * 60 * 5,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: false,
        ...queryOptions
    });
    //
    const requestData = (data as unknown) as IRequest<T>;

    //valores importantes
    const tableData = useMemo(() => initialData || requestData?.data || [], [initialData, requestData]);

    const sortedData = useMemo(() => {
        if (pagination === "server") return tableData;

        if (!sortConfig) return tableData;

        return [...tableData].sort((a, b) => {
            const aValue = String(sortConfig.field).split('.').reduce((acc, curr) => acc[curr], a);
            const bValue = String(sortConfig.field).split('.').reduce((acc, curr) => acc[curr], b);

            if (aValue === bValue) return 0;
            if (sortConfig.direction === 'asc') {
                return aValue < bValue ? -1 : 1;
            }
            return aValue > bValue ? -1 : 1;
        });
    }, [tableData, sortConfig, pagination]);

    const paginatedData = useMemo(() => {
        if (pagination === "server") return sortedData;
        const start = page * rowsPerPage;
        return sortedData?.slice(start, start + rowsPerPage) || [];
    }, [sortedData, page, rowsPerPage, pagination]);
    //

    //funções manipuladoras
    const getSelectedRows = useCallback(() => {
        return selectedRows;
    }, [selectedRows]);

    // Expor a função através da ref
    useImperativeHandle(ref, () => ({
        getSelectedRows
    }));

    const handleChangeFilter = useCallback((values: TFilters) => {
        setFilters(values);

        const filteredValues = processFilterValues(values, clearSingleParamUrl);

        if (Object.keys(filteredValues).length === 0) {
            return clearParamsUrl();
        }

        setMultipleParamsUrl(filteredValues);
        handleUpdatePreFilters?.(values);

    }, [handleUpdatePreFilters, setMultipleParamsUrl, clearParamsUrl, clearSingleParamUrl]);

    const handleSort = useCallback((field: string) => {
        setSortConfig(current => ({
            field,
            direction: current?.field === field && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const handleSelectRow = useCallback((row: T) => {
        setSelectedRows(prevSelectedRows => {
            const isSelected = prevSelectedRows.some(item => item.id === row.id);

            let newSelectedRows;
            if (isSelected) {
                newSelectedRows = prevSelectedRows.filter(item => item.id !== row.id);
            } else {
                const selectedRow = selectedFields
                    ? Object.fromEntries(
                        selectedFields.map(field => [field, row[field]])
                    )
                    : row;

                newSelectedRows = [...prevSelectedRows, selectedRow as T];
            }

            // Chama o callback com as linhas atualizadas
            onSelectionChange?.(newSelectedRows);
            return newSelectedRows;
        });
    }, [selectedFields, onSelectionChange]);

    const isRowSelected = useCallback((row: T) => {
        return selectedRows.some(item => item.id === row.id);
    }, [selectedRows]);

    const selectionState = useMemo(() => {
        const someSelected = paginatedData.some(row => selectedRows.some(item => item.id === row.id));
        const allSelected = paginatedData.length > 0 &&
            paginatedData.every(row => selectedRows.some(item => item.id === row.id));

        return {
            indeterminate: someSelected && !allSelected,
            checked: allSelected
        };
    }, [paginatedData, selectedRows]);

    const handleSelectAllClick = useCallback(() => {
        const allCurrentPageSelected = paginatedData.every(row =>
            selectedRows.some(item => item.id === row.id)
        );

        let newSelectedRows;
        if (allCurrentPageSelected) {
            newSelectedRows = selectedRows.filter(item =>
                !paginatedData.some(row => row.id === item.id)
            );
        } else {
            const newSelectedItems = paginatedData.filter(row =>
                !selectedRows.some(item => item.id === row.id)
            ).map(row => {
                if (selectedFields) {
                    return Object.fromEntries(
                        selectedFields.map(field => [field, row[field]])
                    ) as T;
                }
                return row;
            });

            newSelectedRows = [...selectedRows, ...newSelectedItems];
        }

        // Chama o callback com as linhas atualizadas
        onSelectionChange?.(newSelectedRows);
        setSelectedRows(newSelectedRows);
    }, [paginatedData, selectedRows, selectedFields, onSelectionChange]);

    const memoizedOnDoubleClick = useCallback((row: T) => {
        if (onRowDoubleClick) {
            onRowDoubleClick(row);
        }
    }, [onRowDoubleClick]);

    useEffect(() => {
        if (urlParams) {
            const urlFilters = processUrlFilters(preFilledFilters, searchParams);
            setFilters(prev => ({ ...prev, ...urlFilters }));
        }

        if (Object.keys(preFilledFilters).length > 0) {
            setFilters(prev => ({ ...prev, ...preFilledFilters }));
        }
    }, []);

    return (
        <Box sx={styles.container}>
            {(isFetching || externalLoading) && (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                </Box>
            )}
            <TableContainer sx={{ maxHeight: maxHeight }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={styles.headerIndexCell}>#</TableCell>

                            {enableSelection && (
                                <TableCell sx={styles.headerCellCheckbox} padding="checkbox">
                                    <Checkbox
                                        color="primary"
                                        indeterminate={selectionState.indeterminate}
                                        checked={selectionState.checked}
                                        onChange={handleSelectAllClick}
                                    />
                                </TableCell>
                            )}

                            {columns.map((column) => {
                                const filterExist = Object.entries(filters).some(([key, value]) =>
                                    key.startsWith(String(column.id)) &&
                                    value !== null &&
                                    value !== "" &&
                                    value?.length > 0
                                )

                                return (
                                    <TableCell
                                        key={String(column.id)}
                                        align={column.numeric ? 'right' : 'left'}
                                        sx={styles.headerCell}
                                        style={{ minWidth: column.minWidth, width: column.width }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {enableSort && column.sortable ? (
                                                <TableSortLabel
                                                    active={sortConfig?.field === column.id}
                                                    direction={sortConfig?.field === column.id ? sortConfig.direction : 'asc'}
                                                    onClick={() => handleSort(String(column.id))}
                                                >
                                                    {column.label}
                                                </TableSortLabel>
                                            ) : column.label}

                                            {column.filter && (
                                                <FilterAltIcon
                                                    sx={{
                                                        padding: 0,
                                                        margin: 0,
                                                        fontSize: 17,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        color: filterExist ? 'primary.main' : 'gray',
                                                        '&:hover': { color: 'primary.main' }
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenDialogFilters(column)
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedData?.map((row, index) => (
                            <CustomTableRow
                                {...{ index }}
                                key={row.id}
                                row={row}
                                columns={columns}
                                selected={isRowSelected(row)}
                                enableSelection={enableSelection}
                                onSelect={handleSelectRow}
                                onDoubleClick={memoizedOnDoubleClick}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={pagination === "server" ? requestData?.total || 0 : sortedData?.length || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => {
                    setPage(newPage)
                    getLimitAndPage(rowsPerPage, newPage);
                }}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                    getLimitAndPage(parseInt(e.target.value, 10), 0);
                }}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={() => `Pág. ${page + 1}`}
            />

            {
                openDialogFilters && (
                    <DialogHeadFilters<T>
                        {...{ filters }}
                        open={!!openDialogFilters}
                        value={openDialogFilters}
                        handleClose={() => setOpenDialogFilters(null)}
                        handleChangeFilter={handleChangeFilter}
                    />
                )}
        </Box>
    );
}) as <T extends Record<string, any>>(
    props: TableDataGridProps<T> & { ref?: React.ForwardedRef<TableDataGridRef<T>> }
) => JSX.Element;

const styles = {
    container: {
        width: '100%',
        overflow: 'hidden'
    },
    headerIndexCell: {
        bgcolor: '#e3e3e3',
        minWidth: 30,
        border: '1px solid darkgray',
        fontWeight: 'bold',
        textAlign: 'center',
        height: '32px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    headerCellCheckbox: {
        bgcolor: '#e3e3e3',
        borderTop: '1px solid darkgray',
        borderBottom: '1px solid darkgray',
    },
    headerCell: {
        bgcolor: '#e3e3e3',
        borderTop: '1px solid darkgray',
        borderRight: '1px solid darkgray',
        borderBottom: '1px solid darkgray',
        fontWeight: 'bold',
        height: '32px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        padding: '6px 16px',
        boxSizing: 'content-box'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 4
    },
    emptyContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 4
    }
}; 